import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import authService from './services/authService';
import proteinService from './services/proteinService';
import ProteinTooltip from './components/ProteinTooltip';
import "./html5up-theme.css";
import "./EvaluationPage.css";

// Helper function to get CSRF token from cookie (Django default)
const getCsrfToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }
  
  // If no cookie found, try to get it from Django API as fallback
  return fetchCsrfTokenFromAPI();
};

// Fallback function to fetch CSRF token from API
const fetchCsrfTokenFromAPI = async () => {
  try {
    const response = await fetch('/api/csrf-token/', {
      method: 'GET',
      credentials: 'include',
    });
    if (response.ok) {
      const data = await response.json();
      return data.csrfToken;
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token from API:', error);
  }
  return '';
};

// Ensure CSRF token is available (fetch if not in cookies)
const ensureCsrfToken = async () => {
  // Check if token already exists in cookies
  const existingToken = getCsrfToken();
  if (existingToken && typeof existingToken === 'string' && existingToken.length > 0) {
    return existingToken;
  }
  
  // If not, fetch from API
  return await fetchCsrfTokenFromAPI();
};

const EvaluationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  // Get the return URL from location state, fallback to /select
  const returnUrl = location.state?.returnUrl || '/select';

  // Helper function to extract anonymous ID (content hash only) for display
  const getAnonymousDisplayId = (display_id) => {
    if (!display_id) return '';
    
    // For new format: "modelcode.contenthash" -> return only "contenthash"
    if (display_id.includes('.')) {
      const parts = display_id.split('.');
      // Return the last part (content hash) which is the most unique identifier
      return parts[parts.length - 1];
    }
    
    // For old format or single part, return as-is
    return display_id;
  };
  
  const [ratings, setRatings] = useState({
    clarity: 0,
    conciseness: 0,
    correctness: 0,
    citationsContext: 0,
    contribution: 0
  });
  const [hoveredRating, setHoveredRating] = useState(null);
  const [textFeedback, setTextFeedback] = useState("");
  const [evaluationData, setEvaluationData] = useState(null);
  const [progressData, setProgressData] = useState({ current: 1, total: 1, completed: 0, current_output_evaluated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExistingEvaluation, setIsExistingEvaluation] = useState(false);

  // Helper function to extract the first number from a mutation string
  const extractPositionNumber = (mutation) => {
    // Remove common separators and extract the first number found
    const match = mutation.replace(/[-_]/g, '').match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  // Helper function to sort mutations by position number
  const sortMutationsByPosition = (mutations) => {
    if (!mutations || mutations.length === 0) return [];
    
    // Separate mutations with and without numbers
    const withNumbers = [];
    const withoutNumbers = [];
    
    mutations.forEach(mutation => {
      const position = extractPositionNumber(mutation);
      if (position !== null) {
        withNumbers.push({ mutation, position });
      } else {
        withoutNumbers.push(mutation);
      }
    });
    
    // Sort mutations with numbers by their position
    withNumbers.sort((a, b) => a.position - b.position);
    
    // Return sorted mutations with numbers first, then mutations without numbers at the end
    return [...withNumbers.map(item => item.mutation), ...withoutNumbers];
  };

  // Fetch evaluation data when component mounts or ID changes
  useEffect(() => {
    if (id) {
      fetchEvaluationData();
      fetchExistingEvaluation();
      fetchProgressData();
    }
  }, [id]);

  const fetchProgressData = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authentication token if available
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      // Fetch both progress data and total outputs count to ensure consistency
      const [progressResponse, outputsResponse] = await Promise.all([
        fetch(`/api/mutation-evaluations/get_progress/?current_output_id=${id}`, {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        }),
        fetch('/api/mutation-reasoning-outputs/', {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        })
      ]);
      
      console.log('Progress API response status:', progressResponse.status);
      console.log('Outputs API response status:', outputsResponse.status);
      
      // Get total count from the same endpoint as SelectionPage
      let totalCount = 1; // Default fallback
      if (outputsResponse.ok) {
        const outputsData = await outputsResponse.json();
        totalCount = outputsData.length;
        console.log('Total outputs from main API:', totalCount);
      }
      
      if (progressResponse.ok) {
        const progressData = await progressResponse.json();
        console.log('Progress API response data:', progressData);
        setProgressData({
          current: progressData.current,
          total: totalCount, // Use the count from the main outputs endpoint
          completed: progressData.completed,
          current_output_evaluated: progressData.current_output_evaluated || false
        });
      } else {
        const errorText = await progressResponse.text();
        console.error('Progress API error:', progressResponse.status, errorText);
        
        // Even if progress API fails, we can still show meaningful data with the total count
        setProgressData(prev => ({
          ...prev,
          total: totalCount
        }));
      }
    } catch (error) {
      console.error('Error fetching progress data:', error);
      
      // Try to at least get the total count if possible
      try {
        const headers = {
          'Content-Type': 'application/json',
        };
        
        const token = authService.getToken();
        if (token) {
          headers['Authorization'] = `Token ${token}`;
        }
        
        const response = await fetch('/api/mutation-reasoning-outputs/', {
          method: 'GET',
          headers: headers,
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setProgressData(prev => ({
            ...prev,
            total: data.length
          }));
        }
      } catch (fallbackError) {
        console.error('Error in fallback fetch:', fallbackError);
      }
    }
  };

  const fetchExistingEvaluation = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authentication token if available
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      const response = await fetch(`/api/mutation-evaluations/get_user_evaluation/?mutation_reasoning_output_id=${id}`, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.evaluation) {
          // Load existing evaluation data
          const evaluation = data.evaluation;
          setRatings({
            clarity: evaluation.clarity_rating || 0,
            conciseness: evaluation.conciseness_rating || 0,
            correctness: evaluation.accuracy_rating || 0,
            citationsContext: evaluation.completeness_rating || 0,
            contribution: evaluation.biological_relevance_rating || 0
          });
          setTextFeedback(evaluation.additional_comments || "");
          setIsExistingEvaluation(true); // Mark as existing evaluation
        } else {
          // No existing evaluation, reset form
          setRatings({
            clarity: 0,
            conciseness: 0,
            correctness: 0,
            citationsContext: 0,
            contribution: 0
          });
          setTextFeedback("");
          setIsExistingEvaluation(false); // Mark as new evaluation
        }
        setHoveredRating(null);
      }
    } catch (error) {
      console.error('Error fetching existing evaluation:', error);
      // Reset form on error
      setRatings({
        clarity: 0,
        conciseness: 0,
        correctness: 0,
        citationsContext: 0,
        contribution: 0
      });
      setTextFeedback("");
      setIsExistingEvaluation(false); // Mark as new evaluation on error
      setHoveredRating(null);
    }
  };

  const fetchEvaluationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching data for ID: ${id}`);
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authentication token if available
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      const response = await fetch(`/api/mutation-reasoning-outputs/${id}/`, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
      });
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`Failed to fetch evaluation data: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Received data:', data);
      
      // Process data through protein service for caching
      proteinService.processLLMOutputData(data);
      
      setEvaluationData({
        queryContext: {
          outputId: data.display_id,
          virusName: data.virus_name,
          proteinName: data.protein_name,
          proteinSummary: data.protein_summary || ''
        },
        llmOutput: {
          model: data.model_name,
          zoonoticMutations: data.mutation ? [data.mutation] : [],
          reasoning: data.reasoning
        },
        progress: {
          current: 1, // This would be dynamic in a real app
          total: 1, // This would be dynamic in a real app  
          completed: 0 // This would be dynamic in a real app
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleRatingChange = (criterion, rating) => {
    setRatings(prev => ({
      ...prev,
      [criterion]: rating
    }));
  };

  const handleSaveAndGoBack = async () => {
    try {
      setLoading(true);
      
      // Ensure CSRF token is available
      await ensureCsrfToken();
      const csrfToken = getCsrfToken();
      
      const headers = {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      };
      
      // Add authentication token if available
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      const response = await fetch('/api/mutation-evaluations/submit_evaluation/', {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify({
          mutation_reasoning_output_id: id,
          clarity: ratings.clarity,
          conciseness: ratings.conciseness,
          correctness: ratings.correctness,
          citationsContext: ratings.citationsContext,
          contribution: ratings.contribution,
          textFeedback: textFeedback
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save evaluation');
      }

      // Debug logging to help troubleshoot
      console.log('Save completed - isExistingEvaluation:', isExistingEvaluation);
      
      // Show alert ONLY for updates to existing evaluations
      // Do NOT show alert for new evaluation submissions
      if (isExistingEvaluation) {
        alert("Evaluation updated successfully!");
        console.log('Alert shown: existing evaluation updated');
      } else {
        // Silent save for new evaluations - no alert shown
        console.log('New evaluation saved silently - no alert shown');
      }
      navigate(returnUrl);
    } catch (error) {
      console.error('Error saving evaluation:', error);
      alert(`Error saving evaluation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndContinue = async () => {
    try {
      setLoading(true);
      
      // Ensure CSRF token is available
      const csrfToken = await ensureCsrfToken();
      console.log('CSRF token for save and continue:', csrfToken);
      
      const headers = {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      };
      
      // Add authentication token if available
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      // First, save the current evaluation
      const saveResponse = await fetch('/api/mutation-evaluations/submit_evaluation/', {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify({
          mutation_reasoning_output_id: id,
          clarity: ratings.clarity,
          conciseness: ratings.conciseness,
          correctness: ratings.correctness,
          citationsContext: ratings.citationsContext,
          contribution: ratings.contribution,
          textFeedback: textFeedback
        })
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        throw new Error(errorData.error || 'Failed to save evaluation');
      }

      // Then, get a random unevaluated output to mitigate bias
      const nextHeaders = {
        'Content-Type': 'application/json',
      };
      
      // Add authentication token for next request
      if (token) {
        nextHeaders['Authorization'] = `Token ${token}`;
      }
      
      const nextResponse = await fetch('/api/mutation-evaluations/random_unevaluated/', {
        method: 'GET',
        headers: nextHeaders,
        credentials: 'include',
      });

      if (!nextResponse.ok) {
        const errorData = await nextResponse.json();
        throw new Error(errorData.error || 'Failed to get next evaluation');
      }

      const nextData = await nextResponse.json();

      if (nextData.all_completed) {
        alert("Congratulations! You have evaluated all available outputs.");
        navigate(returnUrl);
      } else {
        // Navigate to the randomly selected evaluation, preserving the return URL
        navigate(`/evaluate/${nextData.output.id}`, { 
          state: { returnUrl } 
        });
      }
    } catch (error) {
      console.error('Error saving evaluation or getting next:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Evaluation criteria definitions
  const evaluationCriteria = {
    clarity: {
      label: "Clarity",
      description: "How clearly are ideas and arguments expressed?",
      ratings: {
        1: "Very unclear; incoherent.",
        2: "Often unclear, vague, or disorganized.",
        3: "Inconsistent; there may be some confusing phrasing or unclear ideas.",
        4: "Mostly clear and coherent; some ideas may need refinement.",
        5: "Extremely clear, precise, and easy to follow."
      }
    },
    conciseness: {
      label: "Conciseness",
      description: "Is language efficient and purposeful to convey all relevant information?",
      ratings: {
        1: "Highly redundant with irrelevant wording.",
        2: "Many unnecessary words or off-topic details.",
        3: "Noticeable wordiness or repetition, but no irrelevant information.",
        4: "Generally concise with little repetition.",
        5: "Writing is direct, impactful, and avoids redundancy."
      }
    },
    correctness: {
      label: "Correctness",
      description: "Is the output verifiable against the Ground Truth dataset?",
      ratings: {
        1: "Incorrect or hallucinated mutations or biological reasoning. ",
        2: "One or more mutations are incorrect; biological reasoning is poorly matched or absent.s",
        3: "Mutations and biological reasoning are mostly correct, with some inconsistencies or moderate inaccuracies. ",
        4: "Mutations are correct and verifiable; biological reasoning and scientific explanations are correct, though there might be oversights.",
        5: "Mutations and claims are accurate, and biologically reasoning is correctly interpreted."
      }
    },
    citationsContext: {
      label: "Citations/Context",
      description: "Are citations and context relevant, authentic, and complete?",
      ratings: {
        1: "No valid or relevant citations. Context missing.",
        2: "One or more sources are inaccurate or poorly matched. Context is weak or misrepresented.",
        3: "Some sources are incomplete, outdated, or irrelevant. Context misses key details.",
        4: "Citations are real and complete with minor formatting issues. Context is correct.",
        5: "Citations are authentic, correctly formatted, and relevant. Context is correct and appropriate."
      }
    },
    contribution: {
      label: "Contribution",
      description: "Does the output engender significant or novel insight into the understanding of host-virus interactions?",
      ratings: {
        1: "No meaningful insight provided, or reasoning of mutations is biologically implausible.",
        2: "Response is limited, incomplete, or vague and lacks significance.",
        3: "Response is relevant to the prompt but underexplained.",
        4: "Response has biological relevance and is discussed in context to provide useful insights.",
        5: "Response is insightful and relevant, with clear implications for host-virus interactions."
      }
    }
  };

  const isFormValid = Object.values(ratings).every(rating => rating > 0);

  // Loading state
  if (loading) {
    return (
      <div className="evaluation-page">
        <div className="evaluation-header">
          <div className="loading">Loading evaluation data...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="evaluation-page">
        <div className="evaluation-header">
          <div className="error">Error: {error}</div>
          <button onClick={() => navigate(returnUrl)}>Go Back to Selection</button>
        </div>
      </div>
    );
  }

  // No data state
  if (!evaluationData) {
    return (
      <div className="evaluation-page">
        <div className="evaluation-header">
          <div className="error">No evaluation data found</div>
          <button onClick={() => navigate(returnUrl)}>Go Back to Selection</button>
        </div>
      </div>
    );
  }

  const RatingScale = ({ label, criterion, description, ratingDescriptions, value, onChange }) => (
    <div className="rating-scale">
      <label className="rating-label">{label}: <span className="rating-description-inline">{description}</span></label>
      <div className="rating-options">
        {[1, 2, 3, 4, 5].map(rating => (
          <label 
            key={rating} 
            className="rating-option"
            onMouseEnter={() => setHoveredRating(`${criterion}-${rating}`)}
            onMouseLeave={() => setHoveredRating(null)}
          >
            <input
              type="radio"
              name={criterion}
              value={rating}
              checked={value === rating}
              onChange={() => onChange(criterion, rating)}
              className="rating-radio"
            />
            <span className="rating-number">{rating}</span>
          </label>
        ))}
      </div>
      {/* Show hover description when hovering over an option */}
      {hoveredRating && hoveredRating.startsWith(criterion) && !value && (
        <div className="rating-hover-description">
          {ratingDescriptions[hoveredRating.split('-')[1]]}
        </div>
      )}
      {/* Show selected description when an option is selected */}
      {value > 0 && (
        <div className="rating-selected-description">
          {ratingDescriptions[value]}
        </div>
      )}
    </div>
  );

  return (
    <div className="evaluation-page">
      <div className="evaluation-header">
        <h1>LLM Output Evaluation</h1>
        <div className="header-controls">
          <div className="header-left">
            <div className="progress-indicator">
              {progressData.current_output_evaluated ? (
                // Viewing/editing an already evaluated output
                `Reviewing evaluation ${progressData.completed} of ${progressData.total} (${Math.max(0, progressData.total - progressData.completed)} remaining)`
              ) : (
                // Viewing a new, unevaluated output
                `Evaluation ${progressData.completed + 1} of ${progressData.total} (${Math.max(0, progressData.total - progressData.completed - 1)} remaining)`
              )}
            </div>
            <div className="output-id-indicator">
              Output ID: {getAnonymousDisplayId(evaluationData.queryContext.outputId)}
            </div>
          </div>
          <button 
            className="back-to-list-btn"
            onClick={() => navigate(returnUrl)}
            title="Go back to output list without saving"
          >
            ← Back to List
          </button>
        </div>
      </div>

      <div className="evaluation-content-two-column">
        {/* Left Column: Query Context + LLM Output */}
        <div className="left-column">
          <div className="left-column-scroll">
            {/* Query Context Section */}
            <div className="section compact">
              <h2>Query Context</h2>
              <div className="context-grid-compact">
                <div className="context-item">
                  <strong>Virus:</strong> {evaluationData.queryContext.virusName}
                </div>
                <div className="context-item">
                  <strong>Protein:</strong> 
                  <ProteinTooltip 
                    proteinName={evaluationData.queryContext.proteinName}
                    proteinSummary={evaluationData.queryContext.proteinSummary}
                  >
                    {evaluationData.queryContext.proteinName}
                  </ProteinTooltip>
                </div>
                <div className="context-item">
                  <strong>Mutation:</strong> 
                  {evaluationData.llmOutput.zoonoticMutations && evaluationData.llmOutput.zoonoticMutations.length > 0
                    ? sortMutationsByPosition(evaluationData.llmOutput.zoonoticMutations).join(', ')
                    : 'None specified'
                  }
                </div>
              </div>
            </div>

            {/* LLM Output Section */}
            <div className="section compact">
              <h2>LLM Output</h2>
              
              <div className="output-subsection-compact">
                <h3>Reasoning:</h3>
                <p className="reasoning-text-compact">{evaluationData.llmOutput.reasoning}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Evaluation Form */}
        <div className="right-column">
          <div className="right-column-scroll">
            <div className="section evaluation-form compact">
              <h2>Your Evaluation</h2>
              
              {/* Language/Syntax Group */}
              <div className="evaluation-group language-syntax-group">
                <h3 className="group-label">Language/Syntax</h3>
                <RatingScale
                  key="clarity"
                  label={evaluationCriteria.clarity.label}
                  criterion="clarity"
                  description={evaluationCriteria.clarity.description}
                  ratingDescriptions={evaluationCriteria.clarity.ratings}
                  value={ratings.clarity}
                  onChange={handleRatingChange}
                />
                <RatingScale
                  key="conciseness"
                  label={evaluationCriteria.conciseness.label}
                  criterion="conciseness"
                  description={evaluationCriteria.conciseness.description}
                  ratingDescriptions={evaluationCriteria.conciseness.ratings}
                  value={ratings.conciseness}
                  onChange={handleRatingChange}
                />
              </div>

              {/* Biological Relevance Group */}
              <div className="evaluation-group biological-relevance-group">
                <h3 className="group-label">Biological Relevance</h3>
                <RatingScale
                  key="citationsContext"
                  label={evaluationCriteria.citationsContext.label}
                  criterion="citationsContext"
                  description={evaluationCriteria.citationsContext.description}
                  ratingDescriptions={evaluationCriteria.citationsContext.ratings}
                  value={ratings.citationsContext}
                  onChange={handleRatingChange}
                />
                <RatingScale
                  key="correctness"
                  label={evaluationCriteria.correctness.label}
                  criterion="correctness"
                  description={evaluationCriteria.correctness.description}
                  ratingDescriptions={evaluationCriteria.correctness.ratings}
                  value={ratings.correctness}
                  onChange={handleRatingChange}
                />
                <RatingScale
                  key="contribution"
                  label={evaluationCriteria.contribution.label}
                  criterion="contribution"
                  description={evaluationCriteria.contribution.description}
                  ratingDescriptions={evaluationCriteria.contribution.ratings}
                  value={ratings.contribution}
                  onChange={handleRatingChange}
                />
              </div>

              <div className="text-feedback-section-compact">
                <label htmlFor="textFeedback" className="feedback-label">
                  Additional Comments (Optional):
                </label>
                <textarea
                  id="textFeedback"
                  className="feedback-textarea-compact"
                  rows="3"
                  placeholder="Please provide any additional feedback, suggestions, or observations about this LLM output..."
                  value={textFeedback}
                  onChange={(e) => setTextFeedback(e.target.value)}
                />
              </div>

              <div className="form-actions-compact">
                <div className="button-group">
                  <button 
                    className={`save-back-button ${isFormValid ? 'enabled' : 'disabled'}`}
                    onClick={handleSaveAndGoBack}
                    disabled={!isFormValid}
                  >
                    Save & Go Back to List
                  </button>
                  <button 
                    className={`save-continue-button ${isFormValid ? 'enabled' : 'disabled'}`}
                    onClick={handleSaveAndContinue}
                    disabled={!isFormValid}
                    title="Saves current evaluation and randomly selects next unevaluated output to reduce bias"
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
                      <span>Save & Continue to Next</span>
                      <span style={{ fontSize: '0.75em', opacity: 0.8, marginTop: '2px' }}>(random)</span>
                    </div>
                  </button>
                </div>
                {!isFormValid && (
                  <p className="validation-message">
                    Please provide ratings for all criteria before submitting.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPage;
